// eslint-disable-next-line no-unused-vars
import { h } from 'preact';
import PropTypes from 'prop-types';
import { useState, useRef, useEffect } from 'preact/hooks';

/**
 * Facet expand collapse component.
 * Used to standardize the expand/collapse functionality.
 *
 * @param {object} props Props
 * @returns {object} JSX
 */
const FacetExpandCollapse = (props) => {
	const { group, mainContent, buttonLabel, expandedContent, showButton } = props;
	const [isHidden, setIsHidden] = useState(true);
	const [clicked, setClicked] = useState(false);
	const buttonRef = useRef(null);
	const containerRef = useRef(null);
	const uniqueId = `facet-${group}-expand-collapse`.toLowerCase().replace(' ', '_');

	/**
	 * Handle the open button click event.
	 */
	const onButtonOpenClick = () => {
		setClicked(true);
		setIsHidden(false);
	};

	/**
	 * Handle the close button click event.
	 */
	const onButtonCloseClick = () => {
		setClicked(true);
		setIsHidden(true);
	};

	/**
	 * Handle the container keydown event.
	 *
	 * @param {object} event The even object.
	 */
	const onContainerKeydown = (event) => {
		const { keyCode } = event;

		// Bail if not escape.
		if (keyCode !== 27) {
			return;
		}

		// Hide the container on escape.
		setClicked(true);
		setIsHidden(true);
	};

	useEffect(() => {
		if (!clicked) return;

		if (isHidden) {
			buttonRef.current.focus();
		} else {
			containerRef.current.querySelector('input').focus();
		}

		setClicked(false);
	}, [isHidden, clicked]);

	return (
		<div className="facet__expand-collapse">
			<ul className="m-0 p-0">
				{mainContent}
			</ul>
			<button
				hidden={showButton}
				ref={buttonRef}
				aria-controls={uniqueId}
				aria-expanded={!isHidden}
				onClick={onButtonOpenClick}
				className="btn btn-link btn-sm px-0 text-decoration-none m-0"
				style={isHidden ? '' : 'display: none;'}
				type="button">
				{buttonLabel}
			</button>
			<ul
				ref={containerRef}
				id={uniqueId}
				aria-hidden={isHidden}
				onKeyDown={onContainerKeydown}
				style={isHidden ? 'display: none;' : ''}
				className="facet__expanded-content m-0 p-0">
				{expandedContent}
			</ul>
			<button
				aria-controls={uniqueId}
				aria-expanded={!isHidden}
				onClick={onButtonCloseClick}
				className="btn btn-link btn-sm px-0 text-decoration-none m-0"
				style={isHidden ? 'display: none;' : ''}
				type="button">
				Collapse
			</button>
		</div>
	);
};

FacetExpandCollapse.defaultProps = {
	buttonLabel: '',
};

FacetExpandCollapse.propTypes = {
	showButton: PropTypes.bool,
	buttonLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
	expandedContent: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
	group: PropTypes.string.isRequired,
	mainContent: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
};

export default FacetExpandCollapse;
